export type PayoutSummaryTab = 'needsAttention' | 'inProgress' | 'completed' | 'all';

export type PayoutStatusFilter =
  | 'All Statuses'
  | 'NOT_STARTED'
  | 'INVITE_SENT'
  | 'IN_PROGRESS'
  | 'BLOCKED'
  | 'COMPLETED'
  | 'NOT_APPLICABLE';

export type PayoutStage = 'All' | 'Onboarding' | 'Funding' | 'Distribution';

export enum PayoutWorklistStage {
  Onboarding = 'Onboarding',
  Funding = 'Funding',
  Distribution = 'Distribution',
}

export enum PayoutStageProgressStatus {
  NotStarted = 'Not Started',
  InProgress = 'In Progress',
  Completed = 'Completed',
}

export interface PayoutWorklistStageProgress {
  onboarding: PayoutStageProgressStatus;
  funding: PayoutStageProgressStatus;
  distribution: PayoutStageProgressStatus;
}

export enum PayoutWorklistStatus {
  OnboardingNotStarted = 'Onboarding not started',
  OnboardingLinkConfirmed = 'Onboarding link confirmed by the client',
  PersonalInfoConfirmed = 'Personal info confirmed by the client',
  AccountSetupInProgress = 'Account setup is in progress',
  CipVerificationInProgress = 'CIP verification is in progress',
  DocumentUploadInProgress = 'Document Uploaded, verification is in progress',
  KycVerificationInProgress = 'KYC verification is in progress',
  AccountCreationInProgress = 'Account creation is in progress',
  SettlementProcessingInProgress = 'Settlement processing is in progress',
  OnboardingCompleted = 'Onboarding completed',
  OnboardingExpired = 'Onboarding expired',

  SubmittedToBankForTransferToV = 'Submitted to Bank for Transfer to V',
  FundsDepositedToV = 'Funds Deposited to V',
  Failed = 'Failed',

  ScheduledForTransfer = 'Scheduled For Transfer',
  AchProcessingToClient = 'ACH Processing to Client',
  Paid = 'Paid',
  PaidExternally = 'Paid Externally',
  DistributionCancelled = 'Cancelled',

  CheckPrintingAndMailPreparation = 'Check printing and mail preparation',
  InTheMail = 'In the Mail',
  DeliveredToAddress = 'Delivered to your address',
  CheckCleared = 'Check Cleared',
  StopPaymentInitiated = 'Stop payment has been initiated',
}

export type PayoutStatus = PayoutStatusFilter;

export type PayoutMethod = 'All' | 'ACH' | 'Check' | 'V' | 'Not Applicable';

export type PayoutOwner = 'All' | 'Claimant' | 'Firm' | 'V.Law' | 'Compliance';

export interface PayoutSummary {
  needsAttention: number;
  inProgress: number;
  completed: number;
  all: number;
}

export interface PayoutWorklistItem {
  settlementId: number;
  settlementSequenceId?: number;
  label?: string | null;
  claimant: {
    id: number;
    clientId?: number;
    customerId?: number;
    referenceId?: string | number;
    name: string;
  };
  caseNumber: string;
  settlementReference: string;
  amount: number;
  paymentMethod: string;
  stage: PayoutWorklistStageProgress;
  status: string;
  owner: string;
  nextStep: string | null;
  nextStepOwner: string | null;
  updatedAt: string;
}

export interface PayoutPagination {
  page: number;
  pageSize: number;
  total: number;
}

export interface PayoutWorklistResponse {
  summary: PayoutSummary;
  items: PayoutWorklistItem[];
  pagination: PayoutPagination;
}

export interface PayoutTimelineEvent {
  icon?: string | null;
  title: string;
  timestamp?: string | null;
  metadata?: string | null;
  description?: string | null;
  status?: string | null;
  claimantStep?: string | null;
}

export interface PayoutOnboardingHistoryItem {
  fromState: string;
  toState: string;
  event: string;
  createdAt: string;
}

export interface PayoutOnboardingRequestItem {
  onboardingId?: number;
  refId?: string;
  state?: string;
  isActive?: boolean;
  timestamp?: string | null;
}

export interface PayoutProgressStep {
  label?: string;
  status: string;
  dateTime?: string | null;
  message?: string | null;
  errorMessage?: string | null;
  failureType?: string | null;
  step?: string;
  datetime?: string | null;
  claimantStep?: string | null;
}

export interface PayoutWorklistDetailResponse {
  settlement?: {
    settlementId?: number;
    settlementSequenceId?: number;
    sequenceNo?: number;
    label?: string | null;
    settlementReference?: string;
    amount?: number;
  };
  claimant?: {
    id?: number;
    clientId?: number;
    customerId?: number;
    referenceId?: string | number;
    name?: string;
  };
  case?: {
    id?: number;
    caseNumber?: string;
  };
  payment?: {
    paymentMethod?: string;
    traceNumber?: string | null;
    checkNumber?: string | null;
  };
  stage?: PayoutWorklistStageProgress;
  status?: string;
  owner?: string;
  nextStep?: string | null;
  nextStepOwner?: string | null;
  onboardingProgress?: {
    steps?: PayoutProgressStep[];
    paymentMethod?: string | null;
  };
  payoutProgress?: {
    steps?: PayoutProgressStep[];
    paymentMethod?: string | null;
  };
  onboardingHistory?: PayoutOnboardingHistoryItem[];
  onboardingRequests?: PayoutOnboardingRequestItem[];

  // Backward-compatible optional fields for previous payload variants.
  settlementId?: number;
  caseNumber?: string;
  settlementReference?: string;
  amount?: number;
  paymentMethod?: string;
  updatedAt?: string;
  timeline?: PayoutTimelineEvent[];
  events?: PayoutTimelineEvent[];
}

export interface PayoutWorklistQueryParams {
  page: number;
  pageSize: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  search?: string;
  stage?: string;
  status?: string;
  method?: string;
  nextStepOwner?: string;
  summaryTab?: string;
}

export interface PayoutFilterValues {
  stage: PayoutStage;
  status: PayoutStatus;
  method: PayoutMethod;
}
